import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAdminAnnotations, fetchUserAnnotationTypes, reviewAnnotation } from "../redux/actions/index";
import { MEMOIZED_DOMAIN_URL } from "../components/util/HostUtils";
import _ from "lodash";
import Measure from "react-measure";
import { Select, Button, Checkbox } from "antd";
import moment from "moment";
import OBCSpinner from "components/util/OBC";

const annotationSelector = state => state.admin.annotationReview;
const annotationTypesSelector = state => state.userAnnotationTypes;
const csrfTokenSelector = state => state.csrfToken;

const AnnotationDisplay = ({ annotations, annotationHovered, displayMatchingGroup, reviewStatus, typeFilter, listIsLoading, listCount }) => {

    const [dimensions, setDimensions] = useState({ height: 0, width: 0 });
    const csrfToken = useSelector(csrfTokenSelector);

    const imageSrc = useMemo(() => {
        if (!annotations.length) {
            return ""
        }
        const info = annotations[0]
        let url = `https://raw${MEMOIZED_DOMAIN_URL}${info.image_path}`;
        if(csrfToken){
            url += `?csrf=${csrfToken}`
        }

        return 
    }, [annotations])

    const filteredAnnotations = useMemo(() => {
        if (displayMatchingGroup) {
            return annotations
        } else {
            return annotations.filter(ann => {
                return (typeFilter === "all" || ann.type === typeFilter) && (reviewStatus === "all" || ann.reviewed === reviewStatus);
            })
        }
    }, [displayMatchingGroup, annotations, reviewStatus, typeFilter]);

    const boundingBoxes = useMemo(() => {

        if (!dimensions) {
            return null;
        }

        return filteredAnnotations.map(ann => {
            const width = Math.abs(ann.bounds[0] - ann.bounds[2]) * dimensions.width;
            const height = Math.abs(ann.bounds[1] - ann.bounds[3]) * dimensions.height;
            return <div key={ann.id} className={"BoundingBox" + (annotationHovered === ann.id ? " Active" : "")} style={{ height, width, left: ann.bounds[0] * dimensions.width, top: ann.bounds[1] * dimensions.height }} />
        })

    }, [filteredAnnotations, dimensions, annotationHovered]);

    return (!filteredAnnotations.length && !listIsLoading ?
        <div className="AnnotationContainer">
            {listCount > 0 &&
                <div className="SelectImageLabel">
                    <h2>Select an image to begin reviewing</h2>
                </div>
            }
        </div> :
        <div className="AnnotationContainer">
            <Measure
                bounds
                onResize={contentRect => {
                    setDimensions(contentRect.bounds)
                }}>
                {({ measureRef }) => {
                    return <div className="ImageContainer" ref={measureRef}>
                        <img src={imageSrc} className="AnnotationImage" crossOrigin={"anonymous"} />
                        <div className="BoxesOverlay">
                            {boundingBoxes}
                        </div>
                    </div>
                }}
            </Measure>
        </div>
    )
}

const AnnotationList = ({ annotations, selectedGroup, selectGroup, setAnnotationHovered, displayMatchingGroup, typeFilter, reviewStatus, setListIsLoading, listIsLoading, setListCount }) => {

    const annotationTypes = useSelector(annotationTypesSelector);
    const annotationReview = useSelector(annotationSelector);
    const dispatch = useDispatch()

    const fetchMore = () => {
        const nextPage = annotationReview.nextPage
        dispatch(getAdminAnnotations(nextPage));
    }

    const acceptAll = useCallback((annotations) => {
        // const annotationIDs = annotations.map(ann => ann.id);
        annotations.forEach(annotation => {
            dispatch(reviewAnnotation(annotation.id, true));
        })
    }, [dispatch])

    const filteredAnnotations = useMemo(() => {
        const filteredAnnotations = {}

        Object.keys(annotations).forEach(groupID => {

            if (displayMatchingGroup) {
                let hasMatching = false

                annotations[groupID].forEach(annotation => {
                    if ((typeFilter === "all" || annotation.type === typeFilter) && (reviewStatus === "all" || annotation.reviewed === reviewStatus)) {
                        hasMatching = true
                    }
                })

                if (hasMatching) {
                    filteredAnnotations[groupID] = annotations[groupID]
                }
            } else {
                annotations[groupID].forEach(annotation => {
                    if ((typeFilter === "all" || annotation.type === typeFilter) && (reviewStatus === "all" || annotation.reviewed === reviewStatus)) {
                        if (!filteredAnnotations[groupID]) {
                            filteredAnnotations[groupID] = []
                        }

                        filteredAnnotations[groupID].push(annotation)
                    }
                })
            }
        })
        return filteredAnnotations
    }, [annotations, displayMatchingGroup, reviewStatus, typeFilter])

    const annotationItems = useMemo(() => {
        const annotationsList = Object.keys(filteredAnnotations).flatMap(groupID => {
            const active = selectedGroup === groupID;
            const anns = filteredAnnotations[groupID];
            const amountReviewed = anns.filter(ann => ann.reviewed).length;
            const first = anns[0];

            const items = anns.map(ann => {

                const annType = _.find(annotationTypes, type => type.id === ann.type)

                return (
                    <div key={ann.id} className={"Annotation" + (active ? " Active" : "")} onMouseEnter={() => setAnnotationHovered(ann.id)} onMouseLeave={() => setAnnotationHovered(null)}>
                        <div>
                            <span><strong>User</strong> : {ann.users_name}</span>
                            <span><strong>Type</strong> : {annType ? annType.type : ann.custom_label}</span>
                        </div>

                        <div className="AcceptContainer">
                            <span><strong>Accepted</strong> : <Checkbox checked={ann.reviewed} onChange={(e) => dispatch(reviewAnnotation(ann.id, e.target.checked))} /></span>
                        </div>
                    </div>
                )
            })

            return <div className={"AnnotationItem" + (active ? " Active" : "")}>
                <div className="AnnotationItemHeader">
                    <span><b>#{first.session_id}</b> - {moment.unix(first.session_timestamp).format("DD/MM/YYYY")}</span>
                    <span>{amountReviewed} / {anns.length}</span>
                    {active ? <Button type="primary" onClick={() => acceptAll(anns)}>Accept All</Button>
                        : <Button onClick={() => selectGroup(groupID)}>View</Button>}
                </div>
                {items}
            </div>
        })
        if (annotationsList.length > 0) {
            setListIsLoading(false)
        }
        setListCount(annotationsList.length)

        return annotationsList
    }, [filteredAnnotations, selectGroup, annotationTypes, dispatch, setAnnotationHovered, selectedGroup, acceptAll])


    return (
        <div className="AnnotationList">
            {listIsLoading ?
                <div className="LoadingLogo">
                    <OBCSpinner
                        size={70}
                        speed={3}
                        color={'#e8dfff'}
                    />
                </div> :
                Object.keys(filteredAnnotations).length === 0 ?
                    <div className="NoAnnotations">
                        No Annotations to display
                    </div> :
                    <>
                        {annotationItems}
                        <Button className="FetchMoreButton" onClick={fetchMore}>Fetch More</Button>
                    </>
            }
        </div>
    )

}

const AdminAnnotationReview = () => {

    const dispatch = useDispatch()

    const annotationReview = useSelector(annotationSelector);
    const annotationTypes = useSelector(annotationTypesSelector);

    const [selectedGroup, selectGroup] = useState();
    const [typeFilter, setTypeFilter] = useState("all");
    const [reviewStatus, setReviewStatus] = useState("all");
    const [annotationHovered, setAnnotationHovered] = useState();
    const [displayMatchingGroup, setDisplayMatchingGroup] = useState(true);
    const [listIsLoading, setListIsLoading] = useState(true)
    const [listCount, setListCount] = useState(0)

    useEffect(() => {
        dispatch(fetchUserAnnotationTypes())
        dispatch(getAdminAnnotations())
    }, [dispatch])

    const orderedAnnotations = useMemo(() => {
        return _.orderBy(annotationReview.annotations, "");
    }, [annotationReview.annotations])

    const allAnnotations = useMemo(() => {

        const annotations = {}

        orderedAnnotations.forEach(ann => {
            if (!annotations[ann.group_id]) {
                annotations[ann.group_id] = []
            }
            annotations[ann.group_id].push(ann);
        });

        return annotations

    }, [orderedAnnotations])


    // const filteredAnnotations = useMemo(() => {

    //     if(!displayMatchingGroup){

    //         const annotations = {};

    // orderedAnnotations.filter(ann => {
    //     return (typeFilter === "all" || ann.type === typeFilter) && (reviewStatus === "all" || ann.reviewed === reviewStatus);
    // }).forEach(ann => {
    //     if(!annotations[ann.group_id]){
    //         annotations[ann.group_id] = []
    //     }
    //     annotations[ann.group_id].push(ann);
    // })

    //         return annotations

    //     }else{

    //     }

    // }, [typeFilter, reviewStatus, orderedAnnotations, displayMatchingGroup, allAnnotations])


    const typeFilters = useMemo(() => {
        return annotationTypes.map(type => {
            return <Select.Option value={type.id} key={type.id}>{type.type}</Select.Option>
        })
    }, [annotationTypes]);

    const toggleCheckbox = (e) => {
        setDisplayMatchingGroup(e.target.checked);
    }


    return <div className="AdminAnnotationReview">

        <div className="AdminPageMainHeader FiltersContainer">

            <div className="FilterContainer">
                <p>Annotation Type</p>
                <Select size="small" onChange={setTypeFilter} value={typeFilter} className="FilterSelect">
                    <Select.Option value="all" key="all">All</Select.Option>
                    {typeFilters}
                </Select>
            </div>

            <div className="FilterContainer">
                <p>Review Status</p>
                <Select size="small" onChange={setReviewStatus} value={reviewStatus} className="FilterSelect">
                    <Select.Option value="all" key="all">All</Select.Option>
                    <Select.Option value={true} key="reviewed">Reviewed</Select.Option>
                    <Select.Option value={false} key="not_reviewed">Not Reviewed</Select.Option>
                </Select>
            </div>

            <div className="FilterContainer">
                <Checkbox onChange={toggleCheckbox} checked={displayMatchingGroup} className="Checkbox">
                    Show Images in matched group
                </Checkbox>
            </div>
        </div>

        <div className="AnnotationReveiwMainContent">

            <div className="LeftCol">
                <AnnotationList
                    annotations={allAnnotations}
                    selectGroup={selectGroup}
                    selectedGroup={selectedGroup}
                    setAnnotationHovered={setAnnotationHovered}
                    displayMatchingGroup={displayMatchingGroup}
                    reviewStatus={reviewStatus}
                    typeFilter={typeFilter}
                    setListIsLoading={setListIsLoading}
                    listIsLoading={listIsLoading}
                    setListCount={setListCount}
                />
            </div>

            <AnnotationDisplay
                annotations={_.get(allAnnotations, [selectedGroup], [])}
                annotationHovered={annotationHovered}
                displayMatchingGroup={displayMatchingGroup}
                reviewStatus={reviewStatus}
                typeFilter={typeFilter}
                listIsLoading={listIsLoading}
                listCount={listCount}
            />
        </div>
    </div>
}

export default AdminAnnotationReview;
